import React from "react";
import type { BatchFixedSlot, BatchDynamicSlot } from "../../../../api/academyEnrolments/enrolmentsService";
import type { SelectedSlot } from "../useEnrolmentForm";
import { slotMatchesFixed, slotMatchesDynamic } from "../utils/slotMatch";

interface SlotSectionProps {
  slotType: "fixed" | "dynamic" | string;
  fixedSlots: BatchFixedSlot[];
  dynamicSlots: BatchDynamicSlot[];
  // Fixed: single selection
  selectedSlot: SelectedSlot | null;
  onSlotSelect: (slot: SelectedSlot) => void;
  // Dynamic: multi-selection
  selectedDynamicSlots: SelectedSlot[];
  onDynamicSlotToggle: (slot: SelectedSlot) => void;
  disabled?: boolean;
  sectionNumber?: number;
}

const SlotSection: React.FC<SlotSectionProps> = ({
  slotType,
  fixedSlots,
  dynamicSlots,
  selectedSlot,
  onSlotSelect,
  selectedDynamicSlots,
  onDynamicSlotToggle,
  disabled,
  sectionNumber = 7,
}) => {
  const isFixed = slotType === "fixed";

  return (
    <div className="enrolment-form-section">
      <h6 className="enrolment-section-title">
        <span className="enrolment-section-number">{sectionNumber}</span>
        Select Slot
        <span className="ms-2 text-muted" style={{ fontSize: 11, fontWeight: 400 }}>
          ({isFixed ? "Fixed schedule" : "Dynamic schedule — pick one or more"})
        </span>
      </h6>

      {isFixed ? (
        /* ── Fixed: single-select ── */
        <div className="enrolment-slot-grid">
          {fixedSlots.length === 0 && (
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>No fixed slots available.</p>
          )}
          {fixedSlots.map((slot) => {
            const slotValue: SelectedSlot = {
              days: slot.days,
              startTime: slot.startTime,
              endTime: slot.endTime,
            };
            const isSelected = selectedSlot ? slotMatchesFixed(selectedSlot, slot) : false;

            return (
              <button
                key={slot._id}
                type="button"
                className={`enrolment-slot-card${isSelected ? " selected" : ""}${disabled ? " disabled" : ""}`}
                onClick={() => !disabled && onSlotSelect(slotValue)}
                disabled={disabled}
              >
                <div className="enrolment-slot-time">
                  {slot.startTime} – {slot.endTime}
                </div>
                <div className="enrolment-slot-days">
                  {slot.days.map((d) => (
                    <span key={d} className="enrolment-slot-day-pill">
                      {d.charAt(0).toUpperCase() + d.slice(1, 3)}
                    </span>
                  ))}
                </div>
                {slot.isFreeTrialAvailable && (
                  <span className="enrolment-slot-trial-badge">Free trial</span>
                )}
                {!slot.canEnroll && (
                  <span className="enrolment-slot-full-badge">Full</span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        /* ── Dynamic: multi-select ── */
        <>
          {selectedDynamicSlots.length > 0 && (
            <p className="text-muted mb-2" style={{ fontSize: 12 }}>
              {selectedDynamicSlots.length} slot{selectedDynamicSlots.length > 1 ? "s" : ""} selected — click a slot to deselect.
            </p>
          )}
          <div className="enrolment-slot-grid">
            {dynamicSlots.length === 0 && (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No dynamic slots available.</p>
            )}
            {dynamicSlots.map((slot, i) => {
              const slotValue: SelectedSlot = {
                day: slot.day,
                startTime: slot.startTime,
                endTime: slot.endTime,
              };
              const isSelected = selectedDynamicSlots.some((s) => slotMatchesDynamic(s, slot));

              return (
                <button
                  key={slot._id ?? i}
                  type="button"
                  className={`enrolment-slot-card${isSelected ? " selected" : ""}${disabled ? " disabled" : ""}`}
                  onClick={() => !disabled && onDynamicSlotToggle(slotValue)}
                  disabled={disabled}
                >
                  <div className="enrolment-slot-time">
                    {slot.startTime} – {slot.endTime}
                  </div>
                  <div className="enrolment-slot-days">
                    <span className="enrolment-slot-day-pill">
                      {slot.day.charAt(0).toUpperCase() + slot.day.slice(1)}
                    </span>
                  </div>
                  {isSelected && (
                    <i className="ri-checkbox-circle-fill text-primary ms-auto" style={{ fontSize: 14 }} />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default SlotSection;
